import { fetchSessionMe, postLogout } from "../../lib/api-auth";
import { clearOrganizerSessionClientHint } from "../../lib/organizer-session-client-hint";
import type { Translate } from "../../lib/i18n";

let accountMenuInstanceId = 0;

function nextPanelId(): string {
  accountMenuInstanceId += 1;
  return `qz-account-menu-panel-${accountMenuInstanceId}`;
}

function sessionSummaryLine(
  payload: Readonly<{ user_id: string; display_name?: string | null }>,
): string {
  const name =
    typeof payload.display_name === "string" ? payload.display_name.trim() : "";
  if (name.length > 0) {
    return name;
  }
  return payload.user_id;
}

function createUserCircleIcon(): SVGSVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg") as SVGSVGElement;
  svg.setAttribute("width", "22");
  svg.setAttribute("height", "22");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke-width", "1.65");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  const c = document.createElementNS(ns, "circle");
  c.setAttribute("cx", "12");
  c.setAttribute("cy", "8");
  c.setAttribute("r", "3.25");
  const p = document.createElementNS(ns, "path");
  p.setAttribute("d", "M5 20v-1.2c0-2 3.55-4.3 7-4.3s7 2.28 7 4.3V20");
  svg.appendChild(c);
  svg.appendChild(p);
  return svg;
}

export function isOrganizerSessionPayload(
  data: unknown,
): data is { user_id: string; display_name?: string | null } {
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const rec = data as Record<string, unknown>;
  return typeof rec.user_id === "string" && rec.user_id.length > 0;
}

/**
 * Top-bar account control for signed-in organizers: icon toggle and menu with summary line plus sign-out (**`POST`** **`/api/v1/auth/logout`**).
 *
 * @param translate - Localized captions.
 * @param payload - Session shape from **`fetchSessionMe`**.
 * @returns Wrapper placed in **`qz-app-header__trail`**.
 */
export function createOrganizerAccountMenu(
  translate: Translate,
  payload: Readonly<{ user_id: string; display_name?: string | null }>,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "qz-account-menu";

  const panelId = nextPanelId();
  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "qz-account-menu__toggle";
  toggle.setAttribute("aria-haspopup", "true");
  toggle.setAttribute("aria-expanded", "false");
  toggle.setAttribute("aria-controls", panelId);
  toggle.setAttribute("aria-label", translate("quiz.account.menu_aria_label"));
  toggle.appendChild(createUserCircleIcon());

  const panel = document.createElement("div");
  panel.id = panelId;
  panel.className = "qz-account-menu__panel";
  panel.setAttribute("role", "menu");
  panel.hidden = true;

  const meta = document.createElement("div");
  meta.className = "qz-account-menu__meta";
  meta.textContent = sessionSummaryLine(payload);
  meta.setAttribute("role", "presentation");

  const logoutBtn = document.createElement("button");
  logoutBtn.type = "button";
  logoutBtn.className = "qz-account-menu__logout qz-btn qz-btn--ghost";
  logoutBtn.setAttribute("role", "menuitem");
  logoutBtn.textContent = translate("quiz.home.logout");

  const errLine = document.createElement("p");
  errLine.className = "qz-account-menu__error";
  errLine.hidden = true;
  errLine.setAttribute("aria-live", "polite");

  panel.appendChild(meta);
  panel.appendChild(logoutBtn);
  panel.appendChild(errLine);
  wrap.appendChild(toggle);
  wrap.appendChild(panel);

  let dismissBound = false;

  let closePanel: () => void;

  const onDocPointerDown = (ev: MouseEvent): void => {
    if (
      !panel.hidden &&
      ev.target instanceof Node &&
      !wrap.contains(ev.target)
    ) {
      closePanel();
    }
  };

  const onKeyDown = (ev: KeyboardEvent): void => {
    if (ev.key === "Escape" && !panel.hidden) {
      closePanel();
      toggle.focus();
    }
  };

  const bindGlobalDismiss = (): void => {
    if (!dismissBound) {
      document.addEventListener("pointerdown", onDocPointerDown);
      document.addEventListener("keydown", onKeyDown);
      dismissBound = true;
    }
  };

  const unbindGlobalDismiss = (): void => {
    if (dismissBound) {
      document.removeEventListener("pointerdown", onDocPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      dismissBound = false;
    }
  };

  closePanel = (): void => {
    unbindGlobalDismiss();
    panel.hidden = true;
    toggle.setAttribute("aria-expanded", "false");
  };

  const openPanel = (): void => {
    errLine.hidden = true;
    errLine.textContent = "";
    panel.hidden = false;
    toggle.setAttribute("aria-expanded", "true");
    bindGlobalDismiss();
  };

  const togglePanel = (): void => {
    if (panel.hidden) {
      openPanel();
    } else {
      closePanel();
    }
  };

  toggle.addEventListener("click", (ev) => {
    ev.stopPropagation();
    togglePanel();
  });

  logoutBtn.addEventListener("click", () => {
    void (async () => {
      errLine.hidden = true;
      errLine.textContent = "";
      logoutBtn.disabled = true;
      logoutBtn.textContent = translate("quiz.home.logout_pending");
      try {
        const res = await postLogout();
        if (res.ok) {
          clearOrganizerSessionClientHint();
          window.location.reload();
          return;
        }
        logoutBtn.disabled = false;
        logoutBtn.textContent = translate("quiz.home.logout");
        errLine.textContent = translate("quiz.home.logout_failed");
        errLine.hidden = false;
      } catch {
        logoutBtn.disabled = false;
        logoutBtn.textContent = translate("quiz.home.logout");
        errLine.textContent = translate("quiz.home.logout_failed");
        errLine.hidden = false;
      }
    })();
  });

  return wrap;
}

/**
 * Fetches **`GET`** **`/api/v1/auth/me`** and attaches **`createOrganizerAccountMenu`** when the response is **`200`** with a valid payload.
 *
 * @param slot - Container (**`qz-app-header__trail`** fragment).
 * @param translate - Captions resolver.
 * @param options - Abort signal for **`fetchSessionMe`**.
 */
export async function attachOrganizerAccountMenuIfAuthenticated(
  slot: HTMLElement,
  translate: Translate,
  options: Readonly<{ signal: AbortSignal }>,
): Promise<void> {
  try {
    const res = await fetchSessionMe({ signal: options.signal });
    if (options.signal.aborted) {
      return;
    }
    if (res.status !== 200) {
      return;
    }
    let data: unknown;
    try {
      data = (await res.json()) as unknown;
    } catch {
      return;
    }
    if (!isOrganizerSessionPayload(data)) {
      return;
    }
    slot.replaceChildren(
      createOrganizerAccountMenu(translate, {
        user_id: data.user_id,
        display_name:
          typeof data.display_name === "string" ? data.display_name : undefined,
      }),
    );
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return;
    }
  }
}
